import React, { useState } from "react";
import { Brain, Lock, CheckCircle2, ArrowRight } from "lucide-react";
import { authReset, formatApiErrorDetail } from "@/lib/auth_api";

const C = { bg: "#0B0F14", panel: "#0F1620", border: "#1B2A3A", text: "#E6EDF6", sub: "#A7B4C6", dim: "#6F7D92", accent: "#2EE6D6", bear: "#FF5C6C", bull: "#2BE38B" };

function getToken() {
    const h = window.location.hash || "";
    const q = h.split("?")[1] || "";
    return new URLSearchParams(q).get("token") || "";
}

export default function ResetPasswordPage({ onDone }) {
    const [token] = useState(getToken);
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (pw.length < 6) { setError("Password must be at least 6 characters"); return; }
        if (pw !== pw2) { setError("Passwords do not match"); return; }
        setBusy(true);
        try {
            await authReset(token, pw);
            setDone(true);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4" style={{ background: C.bg, color: C.text }}>
            <div className="w-full max-w-md">
                <div className="flex items-center justify-center gap-2 mb-6">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(46,230,214,0.12)", border: `1px solid ${C.accent}40` }}>
                        <Brain size={20} color={C.accent} />
                    </div>
                    <div className="text-lg font-semibold tracking-wide">GridDesk</div>
                </div>
                <div className="panel p-7 rounded-xl" style={{ background: C.panel, border: `1px solid ${C.border}` }} data-testid="reset-card">
                    {done ? (
                        <div className="text-center space-y-4">
                            <CheckCircle2 size={40} color={C.bull} className="mx-auto" />
                            <div className="text-lg font-semibold">Password updated</div>
                            <div className="text-sm" style={{ color: C.sub }}>You can now sign in with your new password.</div>
                            <button onClick={onDone} className="btn-anim w-full rounded-lg py-3 text-sm font-semibold" style={{ background: C.accent, color: C.bg }} data-testid="reset-goto-login">
                                Go to sign in
                            </button>
                        </div>
                    ) : !token ? (
                        <div className="text-sm" style={{ color: C.bear }} data-testid="reset-no-token">Invalid reset link — no token found.</div>
                    ) : (
                        <form onSubmit={submit} className="space-y-3">
                            <div className="text-xl font-semibold mb-1">Set a new password</div>
                            <div className="text-sm mb-3" style={{ color: C.sub }}>Choose a strong password for your dashboard.</div>
                            {error && <div className="text-sm rounded-md px-3 py-2" style={{ background: "rgba(255,92,108,0.1)", color: C.bear, border: `1px solid ${C.bear}40` }} data-testid="reset-error">{error}</div>}
                            <div className="flex items-center gap-2 rounded-lg px-3" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                                <Lock size={16} color={C.dim} />
                                <input type="password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} className="flex-1 bg-transparent py-3 text-sm outline-none" style={{ color: C.text }} required data-testid="reset-password" />
                            </div>
                            <div className="flex items-center gap-2 rounded-lg px-3" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                                <Lock size={16} color={C.dim} />
                                <input type="password" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="flex-1 bg-transparent py-3 text-sm outline-none" style={{ color: C.text }} required data-testid="reset-password-confirm" />
                            </div>
                            <button type="submit" disabled={busy} className="btn-anim w-full flex items-center justify-center gap-2 rounded-lg py-3 text-sm font-semibold" style={{ background: C.accent, color: C.bg, opacity: busy ? 0.6 : 1 }} data-testid="reset-submit">
                                {busy ? "Updating…" : <>Update password <ArrowRight size={16} /></>}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
